import AutomatedEmails from '@components/AutomatedEmails';
import BlockedAddresses from '@components/BlockedAddresses';
import PageHeader, { PageHeroSlot, usePageHeaderSlots } from '@components/Common/PageHeader';
import DripCampaigns from '@components/DripCampaigns';
import EmailAudit from '@components/EmailAudit';
import EmailTemplates from '@components/EmailTemplates';
import DripSequences from '@components/EmailOperations/DripSequences';
import EmailList from '@components/EmailOperations/EmailList';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './index.scss';

/**
 * Email Operations.
 *
 * `emails` leads: it is the one view that answers "what exists and is it healthy" across every
 * family, and both other lists are reachable from a row on it.
 *
 * Analytics is no longer a tab here — it moved to /dashboard, where the rest of the platform's
 * cross-cutting numbers live. Per-email numbers are on the email record itself, so the tab was
 * answering a question this section no longer needed to.
 */
/**
 * Tab slugs.
 *
 * These used to be `drips` (sequences) and `drip` (enrollments) — two different tabs one letter
 * apart — plus `audit` for a tab labelled "Send Log". Combined with the `?? 0` fallback below, a
 * mistyped or stale slug did not 404: it silently rendered the Emails tab, so the URL and the
 * screen disagreed and nothing said so. The same bug shipped in a test, which spent months
 * asserting against `/cloud-costs/overview` — not a real slug — and therefore against the AWS tab.
 */
export const TAB_SLUG: Record<number, string> = {
  0: 'emails',
  1: 'drip-sequences',
  2: 'automation-rules',
  3: 'drip-enrollments',
  4: 'send-log',
  5: 'templates',
  6: 'blocked-addresses',
};
export const SLUG_TAB: Record<string, number> = {
  emails: 0,
  'drip-sequences': 1,
  'automation-rules': 2,
  'drip-enrollments': 3,
  'send-log': 4,
  templates: 5,
  'blocked-addresses': 6,
};

/**
 * Old slug -> canonical slug. Permanent, not transitional.
 *
 * Bookmarks and pasted links outlive any migration window, so these never expire. Per CLAUDE.md's
 * amended never-change rule: a route may be renamed, but nothing that once worked may stop working.
 */
export const SLUG_ALIASES: Record<string, string> = {
  drips: 'drip-sequences',
  automation: 'automation-rules',
  drip: 'drip-enrollments',
  audit: 'send-log',
};

/** In TAB_SLUG order. */
const PANELS: React.ComponentType[] = [EmailList, DripSequences, AutomatedEmails, DripCampaigns, EmailAudit, EmailTemplates, BlockedAddresses];

const EmailCampaigns: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const slots = usePageHeaderSlots();

  const requested = tab ?? 'emails';
  const canonical = SLUG_ALIASES[requested] ?? requested;
  const activeTab = SLUG_TAB[canonical];

  /*
   * Canonicalise in an effect, never during render.
   *
   * `handleTabChange` also navigates with `replace: true`, and two replace navigations raised in
   * the same render can loop. Doing it here means the URL self-heals after paint: an old link
   * lands on the right tab and the address bar stops showing a name the codebase no longer uses.
   *
   * An unknown slug goes to `emails` rather than falling back to index 0. Those look identical
   * today and are not: the fallback rendered a page whose URL claimed to be something else.
   */
  useEffect(() => {
    if (activeTab === undefined) {
      navigate('/email-campaigns/emails', { replace: true });
    } else if (canonical !== requested) {
      navigate(`/email-campaigns/${canonical}`, { replace: true });
    }
  }, [activeTab, canonical, requested, navigate]);

  const handleTabChange = (newValue: number) => {
    navigate(`/email-campaigns/${TAB_SLUG[newValue]}`, { replace: true });
  };

  /*
   * Nothing renders on an old or unknown slug until the redirect above has run.
   *
   * The panels are render-and-hide, so every panel mounts at once, and one of them writes `?page=0`
   * into the URL on mount. On an alias that write raced the canonicalising navigate: when it
   * landed second it put the alias back (`/automation?page=0`), the effect's inputs had not
   * changed so it never ran again, and the page stayed on a URL the codebase no longer uses.
   * CI caught it intermittently on /automation and /drip. Rendering nothing for that one frame
   * means no panel exists to race.
   */
  if (activeTab === undefined || canonical !== requested) return null;

  return (
    <Box className="ec-root">
      {/*
       * One header for the suite: H1, then the active tab's own sentence and actions, then the
       * tabs. Each tab still writes a PageHero; PageHeroSlot routes the active one into this
       * header instead of drawing a second, card-shaped header under the tabs.
       */}
      <PageHeader
        title="Email Campaigns"
        icon={<SendOutlinedIcon />}
        slots={slots}
        tabs={{
          tabs: [
            { label: 'Emails', icon: <MarkEmailReadOutlinedIcon fontSize="small" /> },
            { label: 'Drip Sequences', icon: <CampaignOutlinedIcon fontSize="small" /> },
            { label: 'Automation Rules', icon: <SendOutlinedIcon fontSize="small" /> },
            { label: 'Drip Enrollments', icon: <CampaignOutlinedIcon fontSize="small" /> },
            { label: 'Send Log', icon: <FormatListBulletedOutlinedIcon fontSize="small" /> },
            { label: 'Templates', icon: <CodeOutlinedIcon fontSize="small" /> },
            { label: 'Blocked addresses', icon: <BlockOutlinedIcon fontSize="small" /> },
          ],
          value: activeTab ?? 0,
          onChange: handleTabChange,
        }}
      />

      {/* Render-and-hide to preserve state on tab switch. Note this mounts every panel at once,
          which is why the email record is a sibling route rather than another panel. */}
      {PANELS.map((Panel, i) => (
        <Box key={i} className="ec-panel" hidden={(activeTab ?? 0) !== i}>
          <PageHeroSlot slots={slots} active={(activeTab ?? 0) === i}>
            <Panel />
          </PageHeroSlot>
        </Box>
      ))}
    </Box>
  );
};

export default EmailCampaigns;
